import { useDroppable } from '@dnd-kit/core'
import type { Deal, PipelineStage } from '../../types'
import { STAGE_EMPTY_TEXT } from '../../types'
import DealCard from './DealCard'

interface PipelineColumnProps {
  stage: PipelineStage
  title: string
  deals: Deal[]
  onAddDeal: () => void
}

const STAGE_CHIP_COLOUR: Record<PipelineStage, string> = {
  lead: 'chip-stage-lead',
  conversation: 'chip-stage-conversation',
  proposal: 'chip-stage-proposal',
  negotiation: 'chip-stage-negotiation',
  won: 'chip-stage-won',
  lost: 'chip-stage-lost',
  nurture: 'chip-stage-nurture',
}

export default function PipelineColumn({
  stage,
  title,
  deals,
  onAddDeal,
}: PipelineColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage,
  })

  const isTerminal = stage === 'won' || stage === 'lost'

  return (
    <div
      ref={setNodeRef}
      className={`
        flex-shrink-0 w-72 min-h-[400px]
        bg-[#F5F5F3] rounded-lg
        flex flex-col
        transition-all duration-150
        ${isOver ? 'ring-2 ring-action-primary ring-offset-2' : ''}
      `}
    >
      <header className="px-3 py-3 flex items-center gap-2">
        <span className={`pipeline-column-chip ${STAGE_CHIP_COLOUR[stage]}`} />
        <h2 className="text-sm font-semibold text-text-primary flex-1">
          {title}
        </h2>
        <span className="text-xs text-text-secondary font-medium bg-bg-surface px-2 py-0.5 rounded">
          {deals.length}
        </span>
      </header>

      <div className="flex-1 px-3 pb-3 space-y-2 overflow-y-auto">
        {deals.length === 0 ? (
          <div className="empty-state py-8">
            <p className="empty-state-text">{STAGE_EMPTY_TEXT[stage]}</p>
          </div>
        ) : (
          deals.map((deal) => (
            <DealCard key={deal.id} deal={deal} stage={stage} />
          ))
        )}
      </div>

      {!isTerminal && (
        <button
          onClick={onAddDeal}
          className="
            w-full px-3 py-2.5
            text-sm text-text-secondary font-medium
            hover:text-text-primary hover:bg-[#EAEAE8]
            border-t border-border
            transition-colors duration-150
            rounded-b-lg
          "
        >
          + Add deal
        </button>
      )}
    </div>
  )
}
